import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import type { NihaoBridgeContent } from "../content/types";

interface HeroSectionProps {
  content: NihaoBridgeContent["hero"];
}

export default function HeroSection({ content }: HeroSectionProps) {
  return (
    <section className="w-full py-16 md:py-20 flex flex-col overflow-hidden relative bg-white">
      <div className="w-full h-full px-[5%] flex flex-col items-center justify-center text-center text-slate-900 relative z-10">
        {/* Coming Soon Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold mb-6"
        >
          <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
          Coming Soon
        </motion.div>

        {/* Brand Name */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 tracking-tight"
        >
          {content.brandName}
        </motion.h1>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-6"
        >
          {content.headline}
        </motion.h2>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-lg md:text-xl max-w-[900px] mb-10 opacity-90 leading-relaxed"
        >
          {content.subheadline}
        </motion.p>

        {/* CTA Button */}
        <motion.a
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.98 }}
          href="#pricing"
          className="inline-flex items-center gap-2 bg-cyan-500 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl hover:bg-cyan-600 transition-all duration-300"
        >
          {content.ctaButton}
          <ArrowRight className="w-5 h-5" />
        </motion.a>
      </div>

      {/* Animated horizontal line */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />
    </section>
  );
}
